import * as Yup from "yup";

export const EMAIL_PROVIDERS = ["smtp", "sendgrid"] as const;
export type EmailProvider = (typeof EMAIL_PROVIDERS)[number];

export const SMTP_PORT_OPTIONS = [
  { value: "587", label: "587 · STARTTLS" },
  { value: "465", label: "465 · SSL" },
] as const;

export type EmailConfigureFormValues = {
  provider: EmailProvider | "";
  fromName: string;
  fromEmail: string;
  smtpHost: string;
  smtpPort: string;
  smtpUser: string;
  smtpPassword: string;
  sendgridApiKey: string;
  testTo: string;
  keepExistingSecret: boolean;
};

export const emailConfigureInitialValues = (
  testTo = ""
): EmailConfigureFormValues => ({
  provider: "",
  fromName: "",
  fromEmail: "",
  smtpHost: "",
  smtpPort: "587",
  smtpUser: "",
  smtpPassword: "",
  sendgridApiKey: "",
  testTo,
  keepExistingSecret: false,
});

export const emailConfigureSchema = Yup.object({
  provider: Yup.string()
    .oneOf([...EMAIL_PROVIDERS], "Choose a provider")
    .required("Choose a provider"),
  fromName: Yup.string().trim().required("From name is required").max(80, "Max 80 characters"),
  fromEmail: Yup.string().trim().email("Enter a valid from email").required("From email is required"),
  smtpHost: Yup.string().when("provider", {
    is: "smtp",
    then: (schema) => schema.trim().required("SMTP host is required"),
    otherwise: (schema) => schema.optional(),
  }),
  smtpPort: Yup.string().when("provider", {
    is: "smtp",
    then: (schema) => schema.oneOf(["587", "465"], "Choose a port").required("Port is required"),
    otherwise: (schema) => schema.optional(),
  }),
  smtpUser: Yup.string().when("provider", {
    is: "smtp",
    then: (schema) => schema.trim().required("SMTP username is required"),
    otherwise: (schema) => schema.optional(),
  }),
  smtpPassword: Yup.string().when(["provider", "keepExistingSecret"], {
    is: (provider: string, keepExistingSecret: boolean) =>
      provider === "smtp" && !keepExistingSecret,
    then: (schema) => schema.required("SMTP password is required"),
    otherwise: (schema) => schema.optional(),
  }),
  sendgridApiKey: Yup.string().when(["provider", "keepExistingSecret"], {
    is: (provider: string, keepExistingSecret: boolean) =>
      provider === "sendgrid" && !keepExistingSecret,
    then: (schema) => schema.trim().required("SendGrid API key is required"),
    otherwise: (schema) => schema.optional(),
  }),
  keepExistingSecret: Yup.boolean(),
  testTo: Yup.string().trim().email("Enter a valid test email").required("Test recipient is required"),
});

export const EMAIL_CONFIGURE_STEP_FIELDS: Record<number, Array<keyof EmailConfigureFormValues>> = {
  0: ["provider"],
  1: ["fromName", "fromEmail", "smtpHost", "smtpPort", "smtpUser", "smtpPassword", "sendgridApiKey"],
  2: ["testTo"],
};
